/**
 * Server-side HTML sanitizer for the Affiliate Partner Agreement.
 *
 * Whitelist-only: allowed tags are inert semantic blocks; every event
 * handler, `javascript:` URL, `<script>`, `<iframe>`, `<object>`,
 * `<embed>`, `<style>`, `<link>`, and inline `style=` attribute is
 * stripped. Nothing here executes the input HTML — we tokenize and
 * rebuild — so a malicious draft cannot XSS an admin previewing it
 * inside the app (the admin panel still renders through a sanitized
 * blob-URL sandbox, defense in depth).
 *
 * This is deliberately conservative rather than exhaustive; the legal
 * content is authored by staff, not user-generated, so the surface is
 * small and we reject anything unfamiliar.
 */

const ALLOWED_TAGS = new Set([
  "h1", "h2", "h3", "h4", "h5", "h6",
  "p", "br", "hr",
  "ul", "ol", "li",
  "strong", "em", "b", "i", "u", "small", "sub", "sup",
  "blockquote", "pre", "code",
  "table", "thead", "tbody", "tr", "th", "td",
  "a", "span", "div", "section",
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "title", "rel", "target"]),
  span: new Set(["class"]),
  div: new Set(["class"]),
  section: new Set(["class", "aria-label"]),
  th: new Set(["scope"]),
  td: new Set(["colspan", "rowspan"]),
};

const SAFE_URL = /^(https?:|mailto:|tel:|#|\/)/i;

function stripDangerousUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!SAFE_URL.test(trimmed)) return null;
  // Explicit block on javascript: even if it slipped through URL scheme.
  if (/^\s*javascript:/i.test(trimmed)) return null;
  return trimmed;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Very small tokenizer — the input is authored HTML, not adversarial user
// content, but we still enforce a whitelist. We reject unknown tags entirely
// (their text content is preserved as escaped text so the reader still sees
// something rather than the sanitizer silently deleting a clause).
export function sanitizeAgreementHtml(html: string): string {
  const out: string[] = [];
  const stack: string[] = [];
  let i = 0;
  const n = html.length;

  while (i < n) {
    const lt = html.indexOf("<", i);
    if (lt === -1) {
      out.push(escapeHtml(html.slice(i)));
      break;
    }
    if (lt > i) out.push(escapeHtml(html.slice(i, lt)));

    // Block comments / CDATA / doctype outright.
    if (html.startsWith("<!--", lt)) {
      const end = html.indexOf("-->", lt + 4);
      i = end === -1 ? n : end + 3;
      continue;
    }
    if (html.startsWith("<!", lt) || html.startsWith("<?", lt)) {
      const end = html.indexOf(">", lt + 1);
      i = end === -1 ? n : end + 1;
      continue;
    }

    const gt = html.indexOf(">", lt);
    if (gt === -1) {
      out.push(escapeHtml(html.slice(lt)));
      break;
    }
    const raw = html.slice(lt + 1, gt);
    i = gt + 1;

    const isClose = raw.startsWith("/");
    const body = isClose ? raw.slice(1).trim() : raw.trim();
    const m = /^([a-zA-Z][a-zA-Z0-9]*)/.exec(body);
    if (!m) continue;
    const tag = m[1].toLowerCase();

    // Hard-block ever seeing these tags in output even as text — drop
    // their entire subtree contents by skipping to the matching close tag.
    if (tag === "script" || tag === "style" || tag === "iframe" || tag === "object" || tag === "embed") {
      if (!isClose) {
        const closeIdx = html.toLowerCase().indexOf(`</${tag}`, i);
        i = closeIdx === -1 ? n : (html.indexOf(">", closeIdx) + 1 || n);
      }
      continue;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown tag: drop the tag itself, keep the content by continuing.
      continue;
    }

    if (isClose) {
      // Close the tag if it is currently open.
      const idx = stack.lastIndexOf(tag);
      if (idx !== -1) {
        while (stack.length > idx) out.push(`</${stack.pop()}>`);
      }
      continue;
    }

    // Parse attributes conservatively.
    const attrsStr = body.slice(m[0].length);
    const attrRe = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
    const rebuilt: string[] = [];
    let attrMatch: RegExpExecArray | null;
    while ((attrMatch = attrRe.exec(attrsStr))) {
      const name = attrMatch[1].toLowerCase();
      const value = attrMatch[3] ?? attrMatch[4] ?? attrMatch[5] ?? "";
      if (name.startsWith("on")) continue;                     // event handlers
      if (name === "style") continue;                          // inline styles
      if (name === "srcdoc" || name === "src" || name === "background") continue;
      const allowed = ALLOWED_ATTRS[tag];
      if (!allowed || !allowed.has(name)) continue;
      if (name === "href") {
        const safe = stripDangerousUrl(value);
        if (!safe) continue;
        rebuilt.push(`href="${escapeHtml(safe)}"`);
        continue;
      }
      if (name === "target") {
        if (value !== "_blank") continue;
        rebuilt.push(`target="_blank"`);
        continue;
      }
      if (name === "rel") {
        // Normalise external-link rel for safety.
        rebuilt.push(`rel="noopener noreferrer nofollow"`);
        continue;
      }
      rebuilt.push(`${name}="${escapeHtml(value)}"`);
    }

    // For <a target=_blank>, force noopener rel if the author omitted it.
    if (tag === "a" && /target="_blank"/i.test(rebuilt.join(" ")) && !rebuilt.some((a) => a.startsWith("rel="))) {
      rebuilt.push(`rel="noopener noreferrer nofollow"`);
    }

    // Void elements.
    const voidTags = new Set(["br", "hr"]);
    const attrOut = rebuilt.length ? " " + rebuilt.join(" ") : "";
    if (voidTags.has(tag)) {
      out.push(`<${tag}${attrOut}/>`);
      continue;
    }

    out.push(`<${tag}${attrOut}>`);
    stack.push(tag);
  }

  while (stack.length) out.push(`</${stack.pop()}>`);
  return out.join("");
}

/**
 * Line-by-line diff for admin-side comparison. Returns an array of
 * `{ kind, text }` tuples; kind is 'add' | 'del' | 'same'. Uses a
 * simple LCS-style algorithm — fine for a few thousand lines of
 * agreement text.
 */
export type DiffLine = { kind: "add" | "del" | "same"; text: string };

export function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split(/\r?\n/);
  const b = newText.split(/\r?\n/);
  const m = a.length, n = b.length;
  const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { out.push({ kind: "same", text: a[i] }); i++; j++; }
    else if (lcs[i + 1][j] >= lcs[i][j + 1]) { out.push({ kind: "del", text: a[i++] }); }
    else { out.push({ kind: "add", text: b[j++] }); }
  }
  while (i < m) out.push({ kind: "del", text: a[i++] });
  while (j < n) out.push({ kind: "add", text: b[j++] });
  return out;
}
